import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../../services/firebase/config'
import type { TopicNote } from '../../types'

interface CrossTopicOverlap {
  label: string
  topics: { topicId: string; topicTitle: string }[]
}

interface CrossTopicViewProps {
  userId: string
  journeyId: string
}

export function CrossTopicView({ userId, journeyId }: CrossTopicViewProps) {
  const { t } = useTranslation('preparation')
  const [overlaps, setOverlaps] = useState<CrossTopicOverlap[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function findOverlaps() {
      setLoading(true)
      try {
        // NOTE: This composite query on (userId, journeyId) requires a Firestore index.
        // Firebase will auto-suggest creating it when this query first runs in a new environment.
        const notesQuery = query(
          collection(db, 'topicNotes'),
          where('userId', '==', userId),
          where('journeyId', '==', journeyId)
        )
        const snapshot = await getDocs(notesQuery)
        const notes = snapshot.docs.map(d => d.data() as TopicNote)

        // Build label → topics map (case-insensitive)
        const labelMap = new Map<string, { topicId: string; topicTitle: string }[]>()
        for (const note of notes) {
          if (!note.conceptMap?.nodes) continue
          for (const node of note.conceptMap.nodes) {
            const key = node.label.toLowerCase()
            const existing = labelMap.get(key) ?? []
            if (!existing.some(t => t.topicId === note.topicId)) {
              existing.push({ topicId: note.topicId, topicTitle: note.topicTitle })
            }
            labelMap.set(key, existing)
          }
        }

        // Filter to labels appearing in 2+ topics
        const results: CrossTopicOverlap[] = []
        for (const [label, topics] of labelMap) {
          if (topics.length >= 2) {
            results.push({
              label: label.charAt(0).toUpperCase() + label.slice(1),
              topics,
            })
          }
        }
        results.sort((a, b) => b.topics.length - a.topics.length)
        setOverlaps(results)
      } catch (error) {
        console.error('CrossTopicView: failed to load overlaps', error)
      } finally {
        setLoading(false)
      }
    }
    findOverlaps()
  }, [userId, journeyId])

  if (loading) return null
  if (overlaps.length === 0) return null

  return (
    <div className="mt-6 p-4 bg-gray-900/50 rounded-lg border border-gray-800">
      <h3 className="text-sm font-semibold text-gray-300 mb-3">
        {t('crossTopic.title')}
      </h3>
      <div className="space-y-2">
        {overlaps.map(overlap => (
          <div key={overlap.label} className="flex items-baseline gap-2 text-sm">
            <span className="font-medium text-gray-200">{overlap.label}</span>
            <span className="text-gray-500">—</span>
            <span className="text-gray-400">
              {t('crossTopic.foundIn')}: {overlap.topics.map(t => t.topicTitle).join(', ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
